import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Fade,
  Stack,
  useMediaQuery,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../../app/context/AuthContext";
import { useClient } from "../../app/context/ClientContext";
import { useAlert } from "../../app/context/AlertContext";
import { useNavigate } from "react-router-dom";
import menuItemStyle from "./styles";
import ThemeUploadModal from "./ThemeUploadModal";
import {
  desktopNavMediaQuery,
  mobileNavMediaQuery,
  SIDEBAR_COLLAPSED_WIDTH_PX,
  SAFE_AREA_BOTTOM,
} from "../layoutMetrics";
import { getProfile } from "../../core/services/profile.service";
import { isAdminUser } from "../../core/utils/util";
import { useSidebar } from "../SidebarContext";
import PageCloseButton from "../../pages/shared/PageCloseButton";

const getClientId = (client) =>
  client?.clientId ?? client?.Id ?? client?.id ?? null;

const scrollableMenuSx = {
  overflowY: "auto",
  maxHeight: "auto",
  flexShrink: 0,
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.25)",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(255,255,255,0.4)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,255,255,0.25) transparent",
};

export default function Footer() {
  const { userInfo, logout } = useAuth();
  const { selectedClient } = useClient();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const isDesktopNav = useMediaQuery(desktopNavMediaQuery);
  const { isSidebarOpen } = useSidebar();
  const isCollapsedRail = isDesktopNav && !isSidebarOpen;
  const profileImageUrlRef = useRef("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const open = Boolean(anchorEl);
  const clientId = getClientId(selectedClient);
  const clientName =
    selectedClient?.clientName ??
    selectedClient?.Client_Name ??
    selectedClient?.name ??
    "";

  const revokeProfileImageUrl = useCallback(() => {
    if (profileImageUrlRef.current) {
      URL.revokeObjectURL(profileImageUrlRef.current);
      profileImageUrlRef.current = "";
    }
  }, []);

  const loadProfileImage = useCallback(async () => {
    try {
      const res = await getProfile();
      const blob = res?.data;

      if (!(blob instanceof Blob) || blob.size === 0) return;

      const objectUrl = URL.createObjectURL(blob);
      revokeProfileImageUrl();
      profileImageUrlRef.current = objectUrl;
      setProfileImageUrl(objectUrl);
    } catch {
      revokeProfileImageUrl();
      setProfileImageUrl("");
    }
  }, [revokeProfileImageUrl]);

  useEffect(() => {
    if (!userInfo?.id) {
      revokeProfileImageUrl();
      setProfileImageUrl("");
      return undefined;
    }

    loadProfileImage();
    window.addEventListener("profile-image-updated", loadProfileImage);

    return () => {
      window.removeEventListener("profile-image-updated", loadProfileImage);
      revokeProfileImageUrl();
    };
  }, [loadProfileImage, revokeProfileImageUrl, userInfo?.id]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const goTo = (path) => {
    handleClose();
    navigate(path);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate("/login");
  };

  // const handleOpenThemeModal = () => {
  //   handleClose();
  //   setThemeModalOpen(true);
  // };

  // const handleCloseThemeModal = () => {
  //   setThemeModalOpen(false);
  // };

  const userInitial = userInfo?.username?.charAt(0).toUpperCase() || "U";

  const isAdmin = isAdminUser(userInfo);

  return (
    <Box
      sx={{
        width: isCollapsedRail ? SIDEBAR_COLLAPSED_WIDTH_PX : "100%",
        py: 1,
        ml: 0,
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
        [`@media ${desktopNavMediaQuery}`]: !isCollapsedRail
          ? {
              ml: 1.2,
            }
          : {},
        [`@media ${mobileNavMediaQuery}`]: {
          width: "100%",
          justifyContent: "stretch",
          px: 1,
          pt: 1,
          pb: `max(8px, ${SAFE_AREA_BOTTOM})`,
          borderTop: "1px solid rgba(255,255,255,0.1)",
        },
      }}
    >
      <Box
        onClick={handleOpen}
        // sx={{
        //   display: "flex",
        //   alignItems: "center",
        //   justifyContent: "center",
        //   gap: isCollapsedRail ? 0 : 1.2,
        //   px: isCollapsedRail ? 0.5 : 1.8,
        //   py: 0.7,
        //   minWidth: isCollapsedRail ? 40 : "auto",
        //   borderRadius: isCollapsedRail ? "999px" : "20px",
        //   cursor: "pointer",
        //   background: isCollapsedRail ? "transparent" : "rgba(255,255,255,0.05)",
        //   backdropFilter: isCollapsedRail ? "none" : "blur(10px)",
        //   border: isCollapsedRail ? "none" : "1px solid rgba(255,255,255,0.15)",
        //   transition: "all 0.25s ease",
        //   "&:hover": {
        //     background: "rgba(255,255,255,0.08)",
        //     transform: "translateY(-1px)",
        //   },
        // }}
         sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: isCollapsedRail ? 0 : 1.2,
          px: isCollapsedRail ? 0.5 : 0,
          py: 2, //0.7
          minWidth: isCollapsedRail ? 40 : "auto",
          cursor: "pointer",
          background: "transparent",
          border: "none",
          transition: "opacity 0.25s ease",
          "&:hover": {
            opacity: 0.85,
          },
        }}
      >
        <Avatar
          src={profileImageUrl}
          sx={{
            width: 32,
            height: 32,
            fontSize: 14,
            bgcolor: "rgba(255,255,255,0.15)",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {userInitial}
        </Avatar>

        {!isCollapsedRail && (
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#fff",
              letterSpacing: 0.3,
            }}
          >
            {userInfo?.username || "User"}
          </Typography>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: -1,
            minWidth: 200,
            borderRadius: 3,
            background: "rgba(20,20,20,0.95)",
            backdropFilter: "blur(25px)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
            p: 1,
            overflow: "hidden",
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <PageCloseButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
          />
        <Box sx={{ textAlign: "center", py: 1, pr: 4, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 11, opacity: 0.6 }}>
            Signed in as
          </Typography>

          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {userInfo?.username}
          </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1, flexShrink: 0 }} />

        <Box sx={scrollableMenuSx}>
          <MenuItem onClick={() => goTo("/profile")} sx={menuItemStyle}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <PersonOutlineIcon fontSize="small" />
              <Typography fontSize={13}>Profile</Typography>
            </Stack>
          </MenuItem>

          {isAdmin && (
            <>
              <MenuItem onClick={() => goTo("/users")} sx={menuItemStyle}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <GroupOutlinedIcon fontSize="small" />
                  <Typography fontSize={13}>Users</Typography>
                </Stack>
              </MenuItem>

              <MenuItem onClick={() => goTo("/team")} sx={menuItemStyle}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <GroupsOutlinedIcon fontSize="small" />
                  <Typography fontSize={13}>Team</Typography>
                </Stack>
              </MenuItem>

              <MenuItem onClick={() => goTo("/role")} sx={menuItemStyle}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BadgeOutlinedIcon fontSize="small" />
                  <Typography fontSize={13}>Role</Typography>
                </Stack>
              </MenuItem>

              <MenuItem onClick={() => goTo("/job-title")} sx={menuItemStyle}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <WorkHistoryOutlinedIcon fontSize="small" />
                  <Typography fontSize={13}>Job Title</Typography>
                </Stack>
              </MenuItem>

              <MenuItem onClick={() => goTo("/admin/permissions")} sx={menuItemStyle}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BadgeOutlinedIcon fontSize="small" />
                  <Typography fontSize={13}>Permissions</Typography>
                </Stack>
              </MenuItem>

              <MenuItem onClick={() => goTo("/admin/modules")} sx={menuItemStyle}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BadgeOutlinedIcon fontSize="small" />
                  <Typography fontSize={13}>Modules</Typography>
                </Stack>
              </MenuItem>
            </>
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1, flexShrink: 0 }} />

        {/* <MenuItem onClick={handleOpenThemeModal} sx={menuItemStyle}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PaletteOutlinedIcon fontSize="small" />
            <Typography fontSize={13}>Theme Upload</Typography>
          </Stack>
        </MenuItem> */}

        <MenuItem
          onClick={handleLogout}
          sx={{
            ...menuItemStyle,
            "&:hover": {
              background: "rgba(244,67,54,0.12)",
              color: "#ff5252",
            },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LogoutIcon fontSize="small" />
            <Typography fontSize={13}>Logout</Typography>
          </Stack>
        </MenuItem>
      </Menu>
{/* 
      <ThemeUploadModal
        open={themeModalOpen}
        onClose={handleCloseThemeModal}
        clientId={clientId}
        clientName={clientName}
        onError={(title, message) => showAlert(title, message)}
      /> */}
    </Box>
  );
}
